import { Box, Skeleton, styled, Tooltip, tooltipClasses, TooltipProps, Typography } from "@mui/material";
import React, { FC, useMemo } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NoCrashIcon from "@mui/icons-material/NoCrash";

import { ReactComponent as CarEntryIcon } from "../../assets/icons/car-entry.svg";
import { ReactComponent as CarExitIcon } from "../../assets/icons/car-exit.svg";
import { ReactComponent as UptimeIcon } from "../../assets/icons/uptime-icon.svg";
import { ReactComponent as BikeIcon } from "../../assets/icons/bike-icon.svg";
import { ReactComponent as CarIcon } from "../../assets/icons/car-icon.svg";
import { ReactComponent as VehicleIcon } from "../../assets/icons/vehicle.svg";
import { IGetUptimeStatsResponseData, IGetVehicleMetricsResponseData } from "../../interfaces";
import moment from "moment";

interface IVehicleMetricsCardsProps {
	vehicleStats?: IGetVehicleMetricsResponseData;
	uptimeStats?: IGetUptimeStatsResponseData;
	loading?: boolean;
	isRpiDevice?: boolean;
}

type DurationType = "year" | "month" | "day" | "hour" | "minute" | "second";

const MetricsTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(() => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "#D7D1FF",
		color: "black",
		textAlign: "center",
		padding: "1rem",
		fontWeight: 400,
		maxWidth: "240px"
	},
	[`& .${tooltipClasses.arrow}`]: {
		color: "#D7D1FF",
		width: "28px",
		height: "20px"
	},
	[`& .${tooltipClasses.tooltipPlacementTop} .${tooltipClasses.arrow}`]: {
		marginBottom: "-20px"
	}
}));

const VehicleMetricsCards: FC<IVehicleMetricsCardsProps> = (props) => {
	const { vehicleStats, uptimeStats, loading, isRpiDevice } = props;

	const VehicleCounts = useMemo(() => {
		if (vehicleStats) {
			const bikeAuthCount = vehicleStats.entry["2W"].auth + vehicleStats.exit["2W"].auth;
			const carAuthCount = vehicleStats.entry["4W"].auth + vehicleStats.exit["4W"].auth;

			const bikeUnauthCount = vehicleStats.entry["2W"].unauth + vehicleStats.exit["2W"].unauth;
			const carUnauthCount = vehicleStats.entry["4W"].unauth + vehicleStats.exit["4W"].unauth;

			return {
				auth: {
					bike: bikeAuthCount,
					car: carAuthCount,
					total: bikeAuthCount + carAuthCount
				},
				unauth: {
					bike: bikeUnauthCount,
					car: carUnauthCount,
					total: bikeUnauthCount + carUnauthCount
				}
			};
		}

		return {
			auth: { bike: 0, car: 0, total: 0 },
			unauth: { bike: 0, car: 0, total: 0 }
		};
	}, [vehicleStats]);

	function getNextDurationValue(
		duration: moment.Duration,
		currentValue?: DurationType
	): { type: DurationType; value: number } {
		switch (currentValue) {
			case "year":
				return { type: "month", value: duration.months() };

			case "month":
				return { type: "day", value: duration.days() };

			case "day":
				return { type: "hour", value: duration.hours() };

			case "hour":
				return { type: "minute", value: duration.minutes() };

			case "minute":
			case "second":
				return { type: "second", value: duration.seconds() };

			default:
				return { type: "year", value: duration.years() };
		}
	}

	function formatDuration(durationSeconds: number) {
		const durationList: string[] = [];
		const duration = moment.duration(durationSeconds, "seconds");

		const count = 1;

		// return nothing when the duration is falsy or not correctly parsed (P0D)
		if (!duration || duration.toISOString() === "P0D") return "0 min";

		let durationType: DurationType | undefined;

		while (durationList.length < count) {
			const durationDetails = getNextDurationValue(duration, durationType);

			if (durationDetails.value >= 1) {
				const durationValue = Math.floor(durationDetails.value);
				durationList.push(`${durationValue} ${durationDetails.type}${durationValue > 1 ? "s" : ""}`);
			}

			if (durationType === durationDetails.type) break;

			durationType = durationDetails.type;
		}

		if (durationList.length <= 0) return "0 minutes";

		return durationList.join(", ");
	}

	if (loading) {
		return <Skeleton variant="rounded" animation="wave" height="100%" />;
	}

	if (isRpiDevice) {
		return (
			<Box
				className="dashboard-content-section no-padding dashboard-stats-wrapper"
				sx={{
					padding: { xs: "12px", sm: "16px", md: "20px" },
					gap: { xs: "8px", sm: "12px" },
					background: "none",
					boxShadow: "none",
					border: "none"
				}}
			>
				<Box
					className="dashboard-stats-item"
					sx={{
						padding: { xs: "12px", sm: "16px" },
						gap: { xs: "8px", sm: "12px" },
						minHeight: { xs: "80px", sm: "auto" }
					}}
				>
					<Box className="dashboard-stats-item-icon-wrapper success">
						<VehicleIcon width={28} fill="inherit" />
					</Box>

					<Box className="dashboard-stats-item-details-wrapper">
						<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<Typography variant="body1" color="#00000080" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
								Authorized
							</Typography>

							<MetricsTooltip arrow placement="top" title={`Total Authorized - ${VehicleCounts.auth.total}`}>
								<InfoOutlinedIcon
									fontSize="inherit"
									sx={{
										color: "#8D86C3",
										fontSize: { xs: "1rem", sm: "inherit" }
									}}
								/>
							</MetricsTooltip>
						</Box>

						<Box
							className="dashboard-stats-item-content success"
							sx={{
								mt: 1,
								gap: { xs: "8px", sm: "16px" },
								justifyContent: { xs: "space-between", sm: "space-around" }
							}}
						>
							<Box
								className="stats-content-item"
								sx={{
									padding: { xs: "4px 6px", sm: "6px 8px" },
									gap: { xs: "4px", sm: "8px" }
								}}
							>
								<BikeIcon width={24} fill="inherit" />
								<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
									{VehicleCounts.auth.bike}
								</Typography>
							</Box>

							<Box
								className="stats-content-item"
								sx={{
									ml: { xs: 1, sm: 2 },
									padding: { xs: "4px 6px", sm: "6px 8px" },
									gap: { xs: "4px", sm: "8px" }
								}}
							>
								<CarIcon width={24} fill="inherit" />
								<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
									{VehicleCounts.auth.car}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>

				{/* <Divider /> */}

				<Box
					className="dashboard-stats-item"
					sx={{
						padding: { xs: "12px", sm: "16px" },
						gap: { xs: "8px", sm: "12px" },
						minHeight: { xs: "80px", sm: "auto" }
					}}
				>
					<Box className="dashboard-stats-item-icon-wrapper warning">
						<VehicleIcon width={28} fill="inherit" />
					</Box>

					<Box className="dashboard-stats-item-details-wrapper">
						<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<Typography variant="body1" color="#00000080" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
								Unauthorized
							</Typography>

							<MetricsTooltip arrow placement="top" title={`Total Unauthorized - ${VehicleCounts.unauth.total}`}>
								<InfoOutlinedIcon
									fontSize="inherit"
									sx={{
										color: "#8D86C3",
										fontSize: { xs: "1rem", sm: "inherit" }
									}}
								/>
							</MetricsTooltip>
						</Box>

						<Box
							className="dashboard-stats-item-content warning"
							sx={{
								mt: 1,
								gap: { xs: "8px", sm: "16px" },
								justifyContent: { xs: "space-between", sm: "space-around" }
							}}
						>
							<Box
								className="stats-content-item"
								sx={{
									padding: { xs: "4px 6px", sm: "6px 8px" },
									gap: { xs: "4px", sm: "8px" }
								}}
							>
								<BikeIcon width={24} fill="inherit" />
								<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
									{VehicleCounts.unauth.bike}
								</Typography>
							</Box>

							<Box
								className="stats-content-item"
								sx={{
									ml: { xs: 1, sm: 2 },
									padding: { xs: "4px 6px", sm: "6px 8px" },
									gap: { xs: "4px", sm: "8px" }
								}}
							>
								<CarIcon width={24} fill="inherit" />
								<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
									{VehicleCounts.unauth.car}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>

				{/* <Divider /> */}

				<Box
					className="dashboard-stats-item"
					sx={{
						padding: { xs: "12px", sm: "16px" },
						gap: { xs: "8px", sm: "12px" },
						minHeight: { xs: "80px", sm: "auto" }
					}}
				>
					<Box className="dashboard-stats-item-icon-wrapper success">
						<NoCrashIcon color="inherit" />
					</Box>

					<Box className="dashboard-stats-item-details-wrapper">
						<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<Typography variant="body1" color="#00000080" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
								Registered Vehicles
							</Typography>

							<MetricsTooltip arrow placement="top" title="Number of vehicles authorized in the database">
								<InfoOutlinedIcon
									fontSize="inherit"
									sx={{
										color: "#8D86C3",
										fontSize: { xs: "1rem", sm: "inherit" }
									}}
								/>
							</MetricsTooltip>
						</Box>

						<Box className="dashboard-stats-item-percentage-text" sx={{ mt: 0.5 }}>
							<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
								{Intl.NumberFormat("en-IN").format(vehicleStats ? vehicleStats.total.vehicle_sync_count : 0)}
							</Typography>
						</Box>
					</Box>
				</Box>

				{/* <Divider /> */}

				<Box
					className="dashboard-stats-item"
					sx={{
						padding: { xs: "12px", sm: "16px" },
						gap: { xs: "8px", sm: "12px" },
						minHeight: { xs: "80px", sm: "auto" }
					}}
				>
					<Box className="dashboard-stats-item-icon-wrapper purple">
						<UptimeIcon width={28} fill="inherit" />
					</Box>

					<Box className="dashboard-stats-item-details-wrapper">
						<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<Typography variant="body1" color="#00000080" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
								Uptime
							</Typography>

							<MetricsTooltip
								arrow
								placement="top"
								title="Today's uptime calculation might be inaccurate due to internet connectivity fluctuations"
							>
								<InfoOutlinedIcon
									fontSize="inherit"
									sx={{
										color: "#8D86C3",
										fontSize: { xs: "1rem", sm: "inherit" }
									}}
								/>
							</MetricsTooltip>
						</Box>

						<Box className="dashboard-stats-item-percentage-text" sx={{ mt: 0.5 }}>
							<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
								{formatDuration(uptimeStats ? uptimeStats.uptime_duration : 0)}
							</Typography>
							<Typography
								variant="body2"
								fontWeight={500}
								color="var(--color-info-main)"
								sx={{
									ml: { xs: 0.25, sm: 0.5 },
									fontSize: { xs: "0.75rem", sm: "0.875rem" }
								}}
							>
								{`(${uptimeStats ? uptimeStats.uptime_percentage : 0}%)`}
							</Typography>
						</Box>
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			className="dashboard-content-section no-padding dashboard-stats-wrapper"
			sx={{
				padding: { xs: "12px", sm: "16px", md: "20px" },
				gap: { xs: "8px", sm: "12px" }
			}}
		>
			<Box
				className="dashboard-stats-item"
				sx={{
					padding: { xs: "12px", sm: "16px" },
					gap: { xs: "8px", sm: "12px" },
					minHeight: { xs: "80px", sm: "auto" }
				}}
			>
				<Box className="dashboard-stats-item-icon-wrapper success">
					<NoCrashIcon color="inherit" />
				</Box>

				<Box className="dashboard-stats-item-details-wrapper">
					<Typography variant="caption" color="#00000080" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
						Registered Vehicles
					</Typography>
					<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
						{Intl.NumberFormat("en-IN").format(vehicleStats ? vehicleStats.total.vehicle_sync_count : 0)}
					</Typography>
				</Box>
			</Box>

			{/* <Divider /> */}

			<Box
				className="dashboard-stats-item"
				sx={{
					padding: { xs: "12px", sm: "16px" },
					gap: { xs: "8px", sm: "12px" },
					minHeight: { xs: "80px", sm: "auto" }
				}}
			>
				<Box className="dashboard-stats-item-icon-wrapper info">
					<CarEntryIcon width={24} fill="#01579B" />
				</Box>

				<Box className="dashboard-stats-item-details-wrapper">
					<Typography variant="caption" color="#00000080" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
						Total Entries
					</Typography>
					<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
						{Intl.NumberFormat("en-IN").format(vehicleStats ? vehicleStats.total.entry : 0)}
					</Typography>
				</Box>
			</Box>

			{/* <Divider /> */}

			<Box
				className="dashboard-stats-item"
				sx={{
					padding: { xs: "12px", sm: "16px" },
					gap: { xs: "8px", sm: "12px" },
					minHeight: { xs: "80px", sm: "auto" }
				}}
			>
				<Box className="dashboard-stats-item-icon-wrapper error">
					<CarExitIcon width={24} fill="inherit" />
				</Box>

				<Box className="dashboard-stats-item-details-wrapper">
					<Typography variant="caption" color="#00000080" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
						Total Exits
					</Typography>
					<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
						{Intl.NumberFormat("en-IN").format(vehicleStats ? vehicleStats.total.exit : 0)}
					</Typography>
				</Box>
			</Box>

			{/* <Divider /> */}

			<Box
				className="dashboard-stats-item"
				sx={{
					padding: { xs: "12px", sm: "16px" },
					gap: { xs: "8px", sm: "12px" },
					minHeight: { xs: "80px", sm: "auto" }
				}}
			>
				<Box className="dashboard-stats-item-icon-wrapper success">
					<UptimeIcon width={24} fill="inherit" />
				</Box>

				<Box className="dashboard-stats-item-details-wrapper">
					<Typography variant="caption" color="#00000080" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
						Uptime
					</Typography>

					<Box className="dashboard-stats-item-percentage-text">
						<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
							{formatDuration(uptimeStats ? uptimeStats.uptime_duration : 0)}
						</Typography>
						<Typography
							variant="body2"
							fontWeight={500}
							color="var(--color-success-main)"
							sx={{
								ml: { xs: 0.25, sm: 0.5 },
								fontSize: { xs: "0.75rem", sm: "0.875rem" }
							}}
						>
							{`(${uptimeStats ? uptimeStats.uptime_percentage : 0}%)`}
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default VehicleMetricsCards;
